import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ContestantHeader from '../../components/ContestantHeader';
import AudioPlayer from '../../components/AudioPlayer';
import { oversizeMessage, uploadErrorMessage } from '../../utils/audioUpload';
import './contestant.css';

// ---------------------------------------------------------------------------
// Small local helpers (mirrors the dashboard badge components)
// ---------------------------------------------------------------------------

function StatusBadge({ status }) {
  const map = {
    submitted:    'badge badge--submitted',
    approved:     'badge badge--approved',
    withdrawn:    'badge badge--withdrawn',
    disqualified: 'badge badge--disqualified',
  };
  return <span className={map[status] ?? 'badge badge--submitted'}>{status ?? 'unknown'}</span>;
}

function PaymentBadge({ status }) {
  const map = {
    pending: 'badge badge--pending',
    paid:    'badge badge--paid',
    waived:  'badge badge--waived',
  };
  return <span className={map[status] ?? 'badge badge--pending'}>{status ?? 'pending'}</span>;
}

// ---------------------------------------------------------------------------
// ContestantSongReview
// Route: /contestant/songs/:songId
// Pre-populates a form from the stored song, lets the contestant edit and
// save changes, play back any uploaded audio, and permanently delete the song.
// ---------------------------------------------------------------------------

export default function ContestantSongReview() {
  const { songId } = useParams();
  const navigate   = useNavigate();

  // ── Page-level state ────────────────────────────────────────────────────
  const [pageState, setPageState] = useState('loading'); // loading | error | ready
  const [pageError, setPageError] = useState('');

  // ── Loaded data ─────────────────────────────────────────────────────────
  const [song,       setSong]       = useState(null);
  const [categories, setCategories] = useState([]);

  // ── Editable form fields ─────────────────────────────────────────────────
  const [title,      setTitle]      = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [artist,     setArtist]     = useState('');
  const [notes,      setNotes]      = useState('');
  const [lyricsText, setLyricsText] = useState('');

  // ── Form feedback ────────────────────────────────────────────────────────
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving,      setSaving]      = useState(false);
  const [saveMsg,     setSaveMsg]     = useState(null); // { type:'success'|'error', text }

  // ── Delete flow ──────────────────────────────────────────────────────────
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting,      setDeleting]      = useState(false);
  const [deleteError,   setDeleteError]   = useState('');

  // ── Audio upload ─────────────────────────────────────────────────────────
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadAudioError, setUploadAudioError] = useState('');

  // ── Load song + categories in parallel ──────────────────────────────────
  useEffect(() => {
    Promise.all([
      api.get(`/api/songs/${songId}`),
      api.get('/api/songs/categories'),
    ])
      .then(([songRes, catRes]) => {
        const s = songRes.data?.song;
        if (!s) {
          setPageError('Song data not received from server. Please try again.');
          setPageState('error');
          return;
        }
        setSong(s);
        setCategories(catRes.data?.categories ?? []);
        // Pre-populate editable fields
        setTitle(s.title          ?? '');
        setCategoryId(String(s.category_id ?? ''));
        setArtist(s.artist        ?? '');
        setNotes(s.notes          ?? '');
        setLyricsText(s.lyrics_text ?? '');
        setPageState('ready');
      })
      .catch((err) => {
        // Surface the server's specific error message if available,
        // or a sensible fallback otherwise.
        const msg =
          err.response?.data?.error ??
          (err.response
            ? `Request failed with status ${err.response.status}.`
            : 'Unable to reach the server. Please check your connection.');
        setPageError(msg);
        setPageState('error');
      });
  }, [songId]);

  // ── Derived values ───────────────────────────────────────────────────────
  const selectedCat  = categories.find((c) => String(c.id) === categoryId);
  const isWithdrawn  = song?.status === 'withdrawn';
  // Set by the API once a judge has scored this song — the music file is then
  // fixed, though the other fields stay editable.
  const audioLocked  = Boolean(song?.audio_locked);
  // Paid/waived songs can be edited but not deleted
  const canDelete    = song?.payment_status === 'pending' && !isWithdrawn;

  // ── Validation ───────────────────────────────────────────────────────────
  function validate() {
    const errs = {};
    if (!title.trim())   errs.title       = 'Song title is required.';
    if (!categoryId)     errs.category_id = 'Please select a category.';
    if (selectedCat?.requires_lyrics_text && !lyricsText.trim()) {
      errs.lyrics_text = 'Lyrics are required for this category.';
    }
    return errs;
  }

  // ── Save handler ─────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await api.put(`/api/songs/${songId}`, {
        title:       title.trim(),
        category_id: Number(categoryId),
        artist:      artist.trim()      || undefined,
        notes:       notes.trim()       || undefined,
        lyrics_text: lyricsText.trim()  || undefined,
        audio_url:   song?.audio_url    || undefined,
      });
      // Refresh local song so badges stay current
      setSong((prev) => ({ ...prev, ...res.data.song }));
      setSaveMsg({ type: 'success', text: 'Changes saved successfully.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSaveMsg({
        type: 'error',
        text: err.response?.data?.error ?? 'Failed to save changes. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete handler ───────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await api.delete(`/api/songs/${songId}`);
      navigate('/contestant/dashboard');
    } catch (err) {
      setDeleteError(err.response?.data?.error ?? 'Failed to delete song. Please try again.');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // ── Audio upload handler ─────────────────────────────────────────────────
  const handleAudioUpload = useCallback(async (file) => {
    if (!file) return;

    // Reject oversized files locally — no point starting an upload that nginx
    // will refuse, and the contestant gets told why immediately.
    const tooBig = oversizeMessage(file);
    if (tooBig) {
      setUploadAudioError(tooBig);
      return;
    }

    setUploadingAudio(true);
    setUploadAudioError('');
    try {
      const formData = new FormData();
      formData.append('audio', file);
      const res = await api.post('/api/songs/upload-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Store the URL in both the song state and the PUT payload
      setSong((prev) => ({ ...prev, audio_url: res.data.audio_url }));
    } catch (err) {
      setUploadAudioError(uploadErrorMessage(err));
    } finally {
      setUploadingAudio(false);
    }
  }, []);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (pageState === 'loading') {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <div className="c-loading">
            <div className="c-spinner" aria-label="Loading…" />
            <p>Loading song…</p>
          </div>
        </main>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (pageState === 'error') {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>
          <div className="c-alert" role="alert">{pageError}</div>
        </main>
      </div>
    );
  }

  // ── Ready ────────────────────────────────────────────────────────────────
  return (
    <div className="c-page">
      <ContestantHeader />

      <main className="c-main">
        <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>

        {/* ── Page heading + status badges ── */}
        <div className="c-page-title-row">
          <div>
            <h1 className="c-page-title">{song?.title}</h1>
            <div className="c-review-badges">
              <StatusBadge  status={song?.status} />
              <PaymentBadge status={song?.payment_status} />
            </div>
          </div>
        </div>

        {/* ── Save result banner ── */}
        {saveMsg && (
          <div
            className={`c-alert${saveMsg.type === 'success' ? ' c-alert--success' : ''}`}
            role="alert"
          >
            {saveMsg.text}
          </div>
        )}

        {/* ── Withdrawn notice ── */}
        {isWithdrawn && (
          <div className="c-alert c-alert--warning" role="status">
            This song has been withdrawn and cannot be edited.
          </div>
        )}

        {/* ── Audio player ── */}
        {song?.audio_url && (
          <div className="c-form-card">
            <AudioPlayer src={song.audio_url} />
          </div>
        )}

        {/* ── Edit form ── */}
        <div className="c-form-card">
          <h2 className="c-form-card__title">Song Details</h2>

          <form onSubmit={handleSave} noValidate>

            {/* Title */}
            <div className="c-form-group">
              <label className="c-label" htmlFor="review-title">
                Song title <span className="c-label__required">*</span>
              </label>
              <input
                id="review-title"
                type="text"
                className={`c-input${fieldErrors.title ? ' is-error' : ''}`}
                value={title}
                onChange={(e) => { setTitle(e.target.value); setSaveMsg(null); }}
                maxLength={255}
                disabled={isWithdrawn}
              />
              {fieldErrors.title && (
                <p className="c-field-error" role="alert">{fieldErrors.title}</p>
              )}
            </div>

            {/* Category */}
            <div className="c-form-group">
              <label className="c-label" htmlFor="review-category">
                Category <span className="c-label__required">*</span>
              </label>
              <select
                id="review-category"
                className={`c-select${fieldErrors.category_id ? ' is-error' : ''}`}
                value={categoryId}
                onChange={(e) => { setCategoryId(e.target.value); setSaveMsg(null); }}
                disabled={isWithdrawn}
              >
                <option value="">— Select a category —</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              {fieldErrors.category_id && (
                <p className="c-field-error" role="alert">{fieldErrors.category_id}</p>
              )}
            </div>

            {/* Artist */}
            <div className="c-form-group">
              <label className="c-label" htmlFor="review-artist">
                Artist / composer <span className="c-label-optional">(optional)</span>
              </label>
              <input
                id="review-artist"
                type="text"
                className="c-input"
                value={artist}
                onChange={(e) => { setArtist(e.target.value); setSaveMsg(null); }}
                maxLength={255}
                disabled={isWithdrawn}
              />
            </div>

            {/* Notes */}
            <div className="c-form-group">
              <label className="c-label" htmlFor="review-notes">
                Notes <span className="c-label-optional">(optional)</span>
              </label>
              <textarea
                id="review-notes"
                className="c-textarea"
                rows={3}
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setSaveMsg(null); }}
                placeholder="Any additional information for the judges…"
                disabled={isWithdrawn}
              />
            </div>

            {/* Conditional: Music file upload */}
            {selectedCat?.requires_music_file && !isWithdrawn && (
              <div className="c-form-group">
                <label className="c-label" htmlFor="review-audio">
                  Music file{' '}
                  <span className="c-label-optional">(MP3, WAV, M4A — max 50 MB)</span>
                </label>

                {/* Already uploaded */}
                {song?.audio_url && !uploadingAudio && (
                  <div className="c-file-success">
                    <span className="c-file-success__icon">🎵</span>
                    <span className="c-file-success__name">
                      {song.audio_url.split('/').pop()}
                    </span>
                    {!audioLocked && (
                      <button
                        type="button"
                        className="c-file-remove-btn"
                        onClick={() => setSong((prev) => ({ ...prev, audio_url: null }))}
                      >
                        ✕ Remove
                      </button>
                    )}
                  </div>
                )}

                {/* Judged songs keep the file they were judged on. */}
                {audioLocked && (
                  <p className="c-field-hint" style={{ color: '#b45309', marginTop: '0.4rem' }}>
                    This song has been judged, so its music file can no longer be
                    changed. Please contact the organiser if the file is wrong.
                  </p>
                )}

                {/* Uploading */}
                {uploadingAudio && (
                  <div className="c-file-uploading">
                    <span className="c-spinner c-spinner--sm" aria-hidden="true" />
                    Uploading…
                  </div>
                )}

                {/* File picker */}
                {!song?.audio_url && !uploadingAudio && !audioLocked && (
                  <label className="c-file-drop" htmlFor="review-audio">
                    <input
                      id="review-audio"
                      type="file"
                      accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,.mp4,audio/*"
                      className="c-file-input"
                      onChange={(e) => handleAudioUpload(e.target.files[0])}
                    />
                    <span className="c-file-drop__icon">📎</span>
                    <span className="c-file-drop__text">
                      Click to choose a file or drag it here
                    </span>
                    <span className="c-file-drop__hint">
                      MP3, WAV, M4A, AAC, OGG, FLAC — max 50 MB
                    </span>
                  </label>
                )}

                {uploadAudioError && (
                  <p className="c-field-error" role="alert">{uploadAudioError}</p>
                )}
              </div>
            )}

            {/* Conditional: Lyrics */}
            {selectedCat?.requires_lyrics_text && (
              <div className="c-form-group">
                <label className="c-label" htmlFor="review-lyrics">
                  Lyrics <span className="c-label__required">*</span>
                </label>
                <p className="c-field-hint">{selectedCat.lyrics_instructions}</p>
                <textarea
                  id="review-lyrics"
                  className={`c-textarea${fieldErrors.lyrics_text ? ' is-error' : ''}`}
                  rows={10}
                  value={lyricsText}
                  onChange={(e) => { setLyricsText(e.target.value); setSaveMsg(null); }}
                  placeholder={selectedCat.lyrics_placeholder}
                  disabled={isWithdrawn}
                />
                {fieldErrors.lyrics_text && (
                  <p className="c-field-error" role="alert">{fieldErrors.lyrics_text}</p>
                )}
              </div>
            )}

            {!isWithdrawn && (
              <button
                type="submit"
                className="c-btn"
                disabled={saving}
                style={{ marginTop: '0.25rem' }}
              >
                {saving ? (
                  <>
                    <span className="c-spinner c-spinner--sm c-spinner--white" aria-hidden="true" />
                    Saving…
                  </>
                ) : (
                  'Save changes'
                )}
              </button>
            )}
          </form>
        </div>

        {/* ── Delete section ── */}
        <div className="c-form-card c-danger-zone">
          <h2 className="c-form-card__title">Delete Song</h2>

          {canDelete ? (
            <>
              <p className="c-danger-zone__desc">
                Permanently remove this song from the competition. This cannot be undone.
              </p>

              {deleteError && (
                <div className="c-alert" role="alert" style={{ marginBottom: '1rem' }}>
                  {deleteError}
                </div>
              )}

              {!confirmDelete ? (
                <button
                  type="button"
                  className="c-btn c-btn--danger"
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete song
                </button>
              ) : (
                <div className="c-delete-confirm">
                  <p className="c-delete-confirm__text">
                    Are you sure you want to delete{' '}
                    <strong>{song?.title}</strong>? This cannot be undone.
                  </p>
                  <div className="c-delete-confirm__actions">
                    <button
                      type="button"
                      className="c-btn c-btn--danger"
                      onClick={handleDelete}
                      disabled={deleting}
                    >
                      {deleting ? (
                        <>
                          <span className="c-spinner c-spinner--sm c-spinner--white" aria-hidden="true" />
                          Deleting…
                        </>
                      ) : (
                        'Yes, delete it'
                      )}
                    </button>
                    <button
                      type="button"
                      className="c-btn c-btn--outline"
                      onClick={() => setConfirmDelete(false)}
                      disabled={deleting}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="c-danger-zone__desc">
              {isWithdrawn
                ? 'This song has already been withdrawn.'
                : 'Songs whose entry fee has been paid cannot be deleted. Please contact the organiser if you need to withdraw.'}
            </p>
          )}
        </div>

        <div style={{ marginTop: '32px' }}>
          <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>
        </div>

      </main>
    </div>
  );
}
